<template>
    <!-- Cost Plus Projects -->
    <VJoyFunnel v-if="projectInformation.contract.type === 'COST_PLUS'" layout-width="large" class="funnel" :class="{isMobileScreen}">
        <template #funnel-header>
            <VJoyFunnelHeader>
                <template #funnel-header-left>
                    <NuxtLink :to="originUrl">
                        <VJoyIconButton size="small" variant="ghost" icon="cross" />
                    </NuxtLink>
                </template>
            </VJoyFunnelHeader>
        </template>
        <template #funnel-stepper>
            <VJoyStepper :step="validationStep">
                <VJoyStep>{{ t('projectclient.activitytracker.navstep.check') }}</VJoyStep>
                <VJoyStep>{{ t('projectclient.billinginformation.navstep.confirm') }}</VJoyStep>
            </VJoyStepper>
        </template>

        <ActivityReport v-if="validationStep === 1" :activity-tracker-information="activityTracker" :project-information="projectInformation" />
        <BillingInformation
            v-else
            v-model="clientCompanyBillingValidationData"
            v-model:additional-document="projectAdditionalDocument"
            :invoice-validation-data="clientCompanyBillingValidationData"
            :start-day="month"
            :is-validation-blocked="isValidationBlocked"
            :r$="r$"
            :form-id="formId"
            @billing-information-updated-successfully="onBillingInformationUpdatedSuccessfully"
            @billing-information-update-failed="onBillingInformationUpdateFailed"
            @billing-information-filled-state-changed="onBillingInformationFilledStateChanged"
        />

        <VJoyFunnelFooter v-if="validationStep === 1">
            <template #footer-right>
                <VJoyButton
                    :label="t('projectclient.activitytracker.page.button.next')"
                    variant="main"
                    size="small"
                    data-testid="project-validation-next-step"
                    @click="navigatePage"
                />
            </template>
        </VJoyFunnelFooter>
        <VJoyFunnelFooter v-else-if="validationStep === 2">
            <template #footer-left>
                <VJoyButton
                    :label="t('projectclient.activitytracker.page.button.back')"
                    variant="secondary"
                    size="small"
                    data-testid="project-validation-previous-step"
                    @click="navigatePage"
                />
            </template>
            <template #footer-right>
                <VJoySubmit
                    v-if="isParticipant"
                    :loading="updatingClientCompanyBillingInformation"
                    data-testid="project-validation-confirm"
                    :disabled="isConfirmationDisabled.disabled"
                    :tooltip="isConfirmationDisabled.reason"
                    :action="saveBillingInformation"
                >
                    {{ t('projectclient.activitytracker.page.button.confirm') }}
                </VJoySubmit>
                <VJoySubmit
                    v-else
                    :loading="updatingClientCompanyBillingInformation"
                    data-testid="project-validation-confirm"
                    :disabled="isConfirmationDisabled.disabled"
                    :tooltip="isConfirmationDisabled.reason"
                    :action="openValidationDialog"
                    :label="t('projectclient.activitytracker.page.button.confirm')"
                />
            </template>
        </VJoyFunnelFooter>
    </VJoyFunnel>

    <!-- Fixed Price Projects -->
    <VJoyFunnel v-else layout-width="large" class="funnel">
        <template #funnel-header>
            <VJoyFunnelHeader>
                <template #funnel-header-left>
                    <NuxtLink :to="originUrl">
                        <VJoyIconButton size="small" variant="ghost" icon="cross" />
                    </NuxtLink>
                </template>
            </VJoyFunnelHeader>
        </template>

        <BillingInformation
            v-model="clientCompanyBillingValidationData"
            v-model:additional-document="projectAdditionalDocument"
            :invoice-validation-data="clientCompanyBillingValidationData"
            :is-validation-blocked="isValidationBlocked"
            :r$="r$"
            :form-id="formId"
            @billing-information-updated-successfully="onBillingInformationUpdatedSuccessfully"
            @billing-information-update-failed="onBillingInformationUpdateFailed"
            @billing-information-filled-state-changed="onBillingInformationFilledStateChanged"
        />

        <VJoyFunnelFooter>
            <template #footer-right>
                <VJoySubmit
                    :loading="updatingClientCompanyBillingInformation || pendingValidation"
                    data-testid="project-validation-confirm"
                    :disabled="isConfirmationDisabled.disabled"
                    :tooltip="isConfirmationDisabled.reason"
                    :action="handleConfirmButtonClick"
                >
                    <span v-if="isParticipant">
                        {{ t('projectclient.billinginformation.page.button.confirm') }}
                    </span>
                    <span v-else>
                        {{ t('projectclient.activitytracker.page.button.confirm') }}
                    </span>
                </VJoySubmit>
            </template>
        </VJoyFunnelFooter>
    </VJoyFunnel>

    <ClientOpsConfirmationModal
        v-if="isClientOpsDialogVisible"
        v-model="nonParticipantValidationComment"
        :header="t('projectclient.activitytracker.page.non.participant.dialog.header')"
        :message="t('projectclient.activitytracker.page.non.participant.dialog.body')"
        @confirm:action="saveBillingInformation"
        @close:dialog="handleShowDialog"
    />

    <VJoyDialog ref="umbrellaCompanyRequestRef" pattern-confirm :confirm-text="t('general.ok')" @dialog:confirm="umbrellaCompanyRequestRef?.hide()">
        <template #dialog-header>
            {{ t('projectclient.billinginformation.umbrella.company.request.dialog.header') }}
        </template>
        <template #dialog-body>
            {{ t('projectclient.billinginformation.umbrella.company.request.dialog.body') }}
        </template>
    </VJoyDialog>
</template>

<script setup lang="ts">
    import {computed, ref, useTemplateRef, watch} from 'vue';
    import {useClientOpsModal, useProjectClientRedirect, useTrackPurchaseOrder} from '@common/composables';
    import {useProjectInformationStore, useScreenSizeStore, useUserStore} from '@common/stores';
    import {ClientOpsConfirmationModal} from '@common/ui';
    import {navigateTo} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {VJoySubmit} from '#components';
    import {useMFEvent, useTranslation} from '#imports';
    import {timeout} from '@malt/utils';
    import {
        pushVJoySnackbar,
        VJoyButton,
        VJoyDialog,
        VJoyFunnel,
        VJoyFunnelFooter,
        VJoyFunnelHeader,
        VJoyIconButton,
        VJoyStep,
        VJoyStepper,
    } from '@maltjoy/core-vue';
    import {type ClientCompanyBillingInfoParam, ContractResourceTypeEnum, type ProjectValidationParam} from '@api';
    import {ContractResourcePaymentModeEnum} from '@api';
    import {useClientCompanyBillingInfoValidationForm} from '../composables';
    import {useActivityTrackerStore, useClientCompanyBillingInformationStore, useProjectValidationStore} from '../stores';
    import {clientCompanyBillingInitData, useGetFullMonthNameAndYear} from '../utils';
    import ActivityReport from './ActivityReport/ActivityReport.vue';
    import BillingInformation from './BillingInformation/BillingInformation.vue';

    const {t} = useTranslation();

    const screenSizeStore = useScreenSizeStore();
    const {isMobileScreen} = storeToRefs(screenSizeStore);

    const umbrellaCompanyRequestRef = useTemplateRef('umbrellaCompanyRequestRef');

    const {resolvePurchaseOrderChange, trackPurchaseOrderAddedOrUpdated, trackPurchaseOrderDeleted} = useTrackPurchaseOrder();

    // --- NEEDED FOR ALL PROJECT TYPES
    // User roles
    const userStore = useUserStore();
    const {roles} = storeToRefs(userStore);

    const freelancer = computed(() => projectInformation.value?.freelancer?.fullName);

    // projectInformation store
    const projectInformationStore = useProjectInformationStore();
    const {projectInformation} = storeToRefs(projectInformationStore);

    const pendingValidation = ref(false);
    const billingInformationFilled = ref(false);

    // --- COST PLUS PROJECTS ONLY (needed for Activity Report validation)

    // activityTracker store
    const activityTrackerStore = useActivityTrackerStore();
    const {activityTracker} = storeToRefs(activityTrackerStore);

    const month = computed(() => useGetFullMonthNameAndYear(new Date(activityTracker.value.startDate)));

    // Funnel navigation
    const validationStep = ref(1);

    function navigatePage() {
        if (validationStep.value === 1) {
            validationStep.value = 2;
        } else {
            validationStep.value = 1;
        }
    }

    const isConfirmationDisabled = computed<{disabled: boolean; reason?: string}>(() => {
        if (isParticipant.value) {
            if (isValidationBlocked.value) {
                return {disabled: true, reason: t('projectclient.activitytracker.page.submit.disabled.validation.blocked')};
            }
            return {disabled: false};
        } else {
            if (!hasAuthorityToValidateProjectCompletion.value) {
                return {disabled: true, reason: t('projectclient.activitytracker.page.submit.disabled.role')};
            } else if (isValidationBlocked.value) {
                return {disabled: true, reason: t('projectclient.activitytracker.page.submit.disabled.validation.blocked')};
            }
            return {disabled: false};
        }
    });

    // Determine authorities + handle clientOpsModal
    const isParticipant = computed(() => projectInformation.value?.participant);

    const hasAuthorityToValidateProjectLot = computed(() => roles.value.includes('ROLE_CRA_VALIDATION'));

    const hasAuthorityToValidateProjectCompletion = computed(() => roles.value.includes('ROLE_MISSION_VALIDATION'));

    const {nonParticipantValidationComment, isClientOpsDialogVisible, handleShowDialog} = useClientOpsModal();

    async function openValidationDialog() {
        const isValid = await validateForm();
        if (isValid) {
            isClientOpsDialogVisible.value = true;
        }
    }

    // Redirection handling

    const fromQuery = ref<string>();
    const {originUrl} = useProjectClientRedirect(fromQuery);

    // check for pending actions that prohibit validation
    const isValidationBlocked = computed(() => !!projectInformation.value.validationInfo?.projectValidationRequirement?.length);

    // clientCompanyBillingInformation store
    const clientCompanyBillingInformationStore = useClientCompanyBillingInformationStore();
    const {updatingClientCompanyBillingInformation, clientCompanyBillingInformation} = storeToRefs(clientCompanyBillingInformationStore);

    // project validation store
    const projectValidationStore = useProjectValidationStore();
    const {shouldDisplayUmbrellaCompanyAffiliationDialog} = storeToRefs(projectValidationStore);

    // Billing Information form
    const clientCompanyBillingValidationData = ref<ClientCompanyBillingInfoParam & Partial<ProjectValidationParam>>(
        clientCompanyBillingInitData(clientCompanyBillingInformation.value),
    );
    clientCompanyBillingValidationData.value = {
        ...clientCompanyBillingValidationData.value,
        // to clean after pushed in production both backend end frontend
        periodStartDate: projectInformation.value.lot.periodStartDate || projectInformation.value.contract.startDate,
        periodEndDate: projectInformation.value.lot.periodEndDate || projectInformation.value.contract.endDate,
        purchaseOrderNumber: projectInformation.value.lot.purchaseOrderNumber,
    };

    const {validateForm, r$, formId} = useClientCompanyBillingInfoValidationForm(clientCompanyBillingValidationData, projectInformation);

    const projectAdditionalDocument = ref<File[]>([]);

    async function handleConfirmButtonClick() {
        if (isParticipant.value) {
            await saveBillingInformation();
        } else {
            await openValidationDialog();
        }
    }

    // Validation of Billing Information
    async function validateProjectInformation(
        projectId: string,
        lotId: string | undefined,
        dueAmount: number,
        paymentMode: ContractResourcePaymentModeEnum,
    ) {
        try {
            const purchaseOrderChange = resolvePurchaseOrderChange(
                projectInformation.value.lot.purchaseOrderNumber,
                clientCompanyBillingValidationData.value.purchaseOrderNumber,
            );
            pendingValidation.value = true;
            await projectValidationStore.validateProject({
                projectId,
                lotId,
                projectValidationParam: {
                    ...clientCompanyBillingValidationData.value,
                    ...(nonParticipantValidationComment.value && {comment: nonParticipantValidationComment.value}),
                },
                additionalDocument: projectAdditionalDocument.value[0],
            });

            const approvalPageSource =
                projectInformation.value.contract.type === ContractResourceTypeEnum.CostPlus
                    ? 'timesheet_approval_page'
                    : 'task_based_project_approval_page';
            if (purchaseOrderChange === 'deleted') {
                await trackPurchaseOrderDeleted(approvalPageSource);
            } else {
                await trackPurchaseOrderAddedOrUpdated(purchaseOrderChange, approvalPageSource, projectInformation.value.lot.purchaseOrderMandatory);
            }

            await timeout(3000);

            if (projectInformation.value.contract.type === 'COST_PLUS') {
                pushVJoySnackbar({
                    props: {
                        message: `<b>${t('projectclient.activitytracker.approval.success.title')}</b></br>${t(
                            'projectclient.activitytracker.approval.success.body',
                            {
                                0: month.value,
                                1: freelancer.value,
                            },
                        )}`,
                        level: 'success',
                    },
                });
            } else {
                pushVJoySnackbar({
                    props: {
                        message: `<b>${t('projectclient.projectinformation.validation.success.title')}</b></br>${t(
                            'projectclient.projectinformation.validation.success.body',
                            {
                                0: freelancer.value,
                            },
                        )}`,
                    },
                });
            }
            if (paymentMode === ContractResourcePaymentModeEnum.Prepayment && dueAmount > 0 && lotId) {
                await navigateTo(`/project-payment/${projectId}/lot/${lotId}`, {external: true});
            } else {
                await navigateTo(originUrl.value);
            }
        } finally {
            pendingValidation.value = false;
        }
    }

    const onBillingInformationUpdatedSuccessfully = async () => {
        await confirmProjectAction();
    };

    const onBillingInformationUpdateFailed = (failureReason: 'INVALID_BILLING_INFORMATION' | 'INSUFFICIENT_PERMISSIONS' | 'SERVER_ERROR') => {
        pushVJoySnackbar({
            props: {
                header: t(`projectclient.billinginformation.invoice.billing.information.error.${failureReason}.header`),
                message: t(`projectclient.billinginformation.invoice.billing.information.error.${failureReason}.message`, [
                    projectInformation.value.client.companyId,
                ]),
                level: 'error',
            },
        });
    };
    const onBillingInformationFilledStateChanged = (state: boolean) => {
        billingInformationFilled.value = state;
    };

    watch(
        shouldDisplayUmbrellaCompanyAffiliationDialog,
        () => {
            if (shouldDisplayUmbrellaCompanyAffiliationDialog.value) {
                umbrellaCompanyRequestRef.value?.show();
            }
            projectValidationStore.setShouldDisplayUmbrellaCompanyAffiliationDialog(false);
        },
        {immediate: true},
    );

    const {dispatchMFEvent} = useMFEvent('compliance-mf-billing-information');
    const saveBillingInformation = async () => {
        dispatchMFEvent('trigger-save');
    };

    // Update billing information then confirm the project action (project validation or project lot validation)
    async function confirmProjectAction() {
        const isValid = await validateForm();

        if (!isValid) {
            if (r$.billingContact.$error) {
                pushVJoySnackbar({
                    props: {
                        header: t('projectclient.billinginformation.invoice.billing.contact.error.header'),
                        message: t('projectclient.billinginformation.invoice.billing.contact.error.message', [
                            projectInformation.value.client.companyId,
                        ]),
                        level: 'error',
                    },
                });
            }
        }

        if (isValid) {
            const {client, projectId, lot, contract} = projectInformation.value;
            const lotId = activityTracker.value?.lotId;

            await clientCompanyBillingInformationStore.updateClientCompanyBillingInformation({
                companyId: client.companyId,
                clientCompanyBillingInfoParam: clientCompanyBillingValidationData.value,
            });
            await validateProjectInformation(projectId, lotId, lot.invoiceDetails.totalIncludingTaxes, contract.paymentMode);
        }
    }
</script>

<style lang="scss" scoped>
    .funnel {
        --funnel-background-color: var(--joy-color-neutral-10);
        --funnel-content-width-large: 1024px;
        --funnel-content-lateral-padding: 0;

        // this is needed to show the bottom sheet (ProjectSummary) on top of the funnel:
        z-index: 29;

        // DS overrides to alignt the funnel stepper correctly
        :deep(.joy-funnel__stepper) {
            width: min(100%, var(--funnel-content-width-large));
            max-width: none;
            padding-inline: var(--joy-core-spacing-4);
            margin-inline: auto;
            box-sizing: border-box;

            @media (min-width: 1024px) {
                padding-inline: 0;
            }

            .joy-stepper {
                width: 100%;
            }
        }
    }
</style>
